import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useList } from '@refinedev/core';
import { CoverImage } from '../../../components/CoverImage.js';
import { humanizeEnum } from '../../../components/Form.js';
import { useCan } from '../../../lib/capabilities.js';

export interface PropertyRow {
  id: string;
  name: string;
  propertyType: string;
  address: { line1: string; city: string; state: string; postalCode: string };
  unitCount: number;
  /** Current owners' display names. Empty means no owner on record. */
  owners: string[];
  /** Presigned GET for the cover photo, or null — the placeholder-image case. */
  photoUrl: string | null;
  archivedAt: string | null;
}

function PropertyCard({ row, onOpen }: { row: PropertyRow; onOpen: () => void }) {
  return (
    <div
      className="property-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <CoverImage src={row.photoUrl} alt={row.name} variant="card" />
      <div className="property-card__body">
        <div className="row">
          <strong>{row.name}</strong>
          {row.archivedAt ? <span className="pill">Archived</span> : null}
        </div>
        <span className="muted">{humanizeEnum(row.propertyType)}</span>
        <address>
          {row.address.line1}, {row.address.city} {row.address.postalCode}
        </address>
        <div className="property-card__meta">
          <span>
            {row.unitCount} unit{row.unitCount === 1 ? '' : 's'}
          </span>
          <span className="muted">
            {row.owners.length > 0 ? row.owners.join(', ') : 'No owner on record'}
          </span>
        </div>
      </div>
    </div>
  );
}

export function PropertyList() {
  const navigate = useNavigate();
  // ACCOUNTING and MAINTENANCE read the portfolio; they do not shape it. The button
  // is absent rather than disabled — see StaffLayout on why.
  const canWrite = useCan('inventory:write');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);

  const pageSize = 25;

  const { data, isLoading, error } = useList<PropertyRow>({
    resource: 'properties',
    pagination: { current: page, pageSize },
    filters: [
      // Refine's CrudFilter shape. The data provider drops any field it does not
      // recognise rather than forwarding it, so an unsupported filter cannot
      // silently return unfiltered rows.
      { field: 'search', operator: 'contains', value: search || undefined },
      {
        field: 'includeArchived',
        operator: 'eq',
        value: includeArchived ? 'true' : undefined,
      },
    ],
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section>
      <div className="row">
        <h1>Properties</h1>
        <span className="shell__spacer" />
        {canWrite ? (
          <button
            type="button"
            className="button"
            onClick={() => void navigate('/staff/properties/new')}
          >
            Add property
          </button>
        ) : null}
      </div>

      <div className="row">
        <input
          className="field__input"
          type="search"
          placeholder="Search name, street or city"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            // Back to page 1: filtering while on page 3 of the old result set lands
            // on a page that may not exist in the new one, which renders as an
            // empty list rather than as no matches.
            setPage(1);
          }}
        />
        <label className="row">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(event) => {
              setIncludeArchived(event.target.checked);
              setPage(1);
            }}
          />
          <span className="muted">Show archived</span>
        </label>
      </div>

      {error ? (
        <p className="alert alert--error">
          {error instanceof Error ? error.message : 'Could not load this list.'}
        </p>
      ) : isLoading ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">
          {search ? `No properties match “${search}”.` : 'No properties yet.'}
        </p>
      ) : (
        <div className="property-grid">
          {rows.map((row) => (
            <PropertyCard
              key={row.id}
              row={row}
              onOpen={() => void navigate(`/staff/properties/${row.id}`)}
            />
          ))}
        </div>
      )}

      {total > pageSize ? (
        <div className="row">
          <button
            type="button"
            className="button button--quiet"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            ← Previous
          </button>
          <span className="muted">
            Page {page} of {lastPage} · {total} total
          </span>
          <button
            type="button"
            className="button button--quiet"
            disabled={page >= lastPage}
            onClick={() => setPage(page + 1)}
          >
            Next →
          </button>
        </div>
      ) : null}
    </section>
  );
}
